import {useCallback, useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import useFilters from '@/hooks/useFilters';
import {adminService} from '@/services/adminService';
import {userService} from '@/services/userService';
import type {
  AfriexAdmin,
  AfriexAdminListProps,
  AfriexAdminQueryParams,
} from '@/types';
import {modifyAdminProfiles} from '@/utils/dashboard';
import {getResponseMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';
const useAdminsList = (): AfriexAdminListProps => {
  const [adminsList, setAdminsList] = useState<AfriexAdmin[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [adminsTotal, setAdminsTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [currentFilters, setCurrentFilters] = useState<AfriexAdminQueryParams>(
    {},
  );
  useFilters(setCurrentFilters);

  const fetchAllAdmins = useCallback(
    async (params?: AfriexAdminQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const admins = await adminService.getAdmins({
          ...params,
          ...currentFilters,
        });
        const userIds = admins?.data?.map((admin) => admin.userId);

        const userProfiles = await Promise.all(
          userIds.map((userId) => userService.getUser(userId)),
        );

        const mergedAdmins = admins.data.map((admin) => {
          const userProfile = userProfiles.find(
            (profile) => profile.id === admin.userId,
          );
          return {
            ...admin,
            fullName: userProfile?.name?.fullName || 'N/A',
          };
        });

        setAdminsList(mergedAdmins);
        setAdminsTotal(admins.total || 0);
      } catch (e) {
        setError(e);
        toast.error(getResponseMessage(e) || 'Failed to fetch Admin');
      } finally {
        setIsLoading(false);
      }
    },
    [currentFilters],
  );

  const handleAutoComplete = useCallback(
    async (searchTerm: string) => {
      try {
        setError(null);
        setIsLoading(true);
        setShowPagination(false);

        if (!searchTerm?.trim() || searchTerm.length < 3) {
          await fetchAllAdmins({});
          return;
        }

        if (!searchTerm.includes('afriex')) {
          setAdminsList([]);
          return;
        }

        const users = await userService.searchAutoComplete(searchTerm);
        const afriexEmailUsers = users.filter(
          (user) => user.currentEmail.split('@')[1] === 'afriex.co',
        );
        const adminProfiles = afriexEmailUsers.length
          ? await Promise.all(
              afriexEmailUsers.map((user) =>
                adminService.getAdmins({userId: user.id}),
              ),
            )
          : [];
        const mergedAdmins = modifyAdminProfiles(adminProfiles, users);
        if (mergedAdmins.length) {
          setAdminsList(mergedAdmins);
          setAdminsTotal(mergedAdmins.length);
          setIsSearch(true);
        }
      } catch (e) {
        setError(e);
        toast.error(getResponseMessage(e) || 'Failed to search Admin');
      } finally {
        setIsLoading(false);
      }
    },
    [fetchAllAdmins],
  );

  // Handle change for instant data fetching
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setIsPageReset((prev) => !prev);
  };

  useEffect(() => {
    // `fetchAllAdmins` try/catches into `setError` + `toast.error`, so this
    // only fires if that reporting itself threw. Dev logger rather than a
    // second toast: the admin has already been told, and a toast is what just
    // failed.
    fetchAllAdmins({}).catch(logger.error);
  }, [fetchAllAdmins]);

  return {
    error,
    fetchAdminsList: fetchAllAdmins,
    adminsList,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: adminsTotal,
    },
    showPagination,
    isPageReset,
    currentFilters,
    clearFilters: cleanFilters,
    selectCurrentFilters,
    handleAutoComplete,
  };
};

export default useAdminsList;
